import React from "react";
import {
  CalendarCheck,
  HeartPulse,
  Pill,
  FlaskConical,
  Receipt,
  QrCode,
  Video,
  Sparkles,
} from "lucide-react";

/* One unified cloud system: a heart in a cloud at the center, with the
   services we offer orbiting around it. Everything, in the cloud. */

const OUTER = [
  { icon: CalendarCheck, a: 0, tone: "green" },
  { icon: HeartPulse, a: 72, tone: "blue" },
  { icon: Pill, a: 144, tone: "green" },
  { icon: FlaskConical, a: 216, tone: "blue" },
  { icon: Receipt, a: 288, tone: "green" },
];

const INNER = [
  { icon: QrCode, a: 40, tone: "blue" },
  { icon: Video, a: 165, tone: "green" },
  { icon: Sparkles, a: 280, tone: "blue" }, // AI
];

function Chip({ icon: Icon, tone, size = 52, counter }) {
  const color = tone === "blue" ? "text-blue-dark" : "text-green";
  return (
    <div className="orbit-chip">
      <div className={counter}>
        <span
          className={`inline-flex items-center justify-center rounded-full bg-white border border-line shadow-[0_10px_26px_-12px_rgba(15,42,71,0.4)] ${color}`}
          style={{ width: size, height: size }}
        >
          <Icon size={size * 0.42} strokeWidth={1.7} />
        </span>
      </div>
    </div>
  );
}

function CloudHeart() {
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <div className="floaty" style={{ width: "48%" }}>
        <svg viewBox="0 0 220 170" className="w-full h-auto overflow-visible" aria-hidden="true">
          <defs>
            <linearGradient id="cloudFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="100%" stopColor="#E9F1F6" />
            </linearGradient>
            <linearGradient id="heartFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#4FB086" />
              <stop offset="100%" stopColor="#2E8560" />
            </linearGradient>
            <filter id="cloudShadow" x="-40%" y="-40%" width="180%" height="180%">
              <feDropShadow dx="0" dy="10" stdDeviation="12" floodColor="#0F2A47" floodOpacity="0.16" />
            </filter>
          </defs>

          {/* soft halo */}
          <circle className="halo-pulse" cx="110" cy="86" r="70" fill="rgba(63,158,114,0.10)" />

          {/* cloud */}
          <g filter="url(#cloudShadow)">
            <ellipse cx="110" cy="112" rx="72" ry="26" fill="url(#cloudFill)" />
            <circle cx="70" cy="98" r="30" fill="url(#cloudFill)" />
            <circle cx="112" cy="80" r="40" fill="url(#cloudFill)" />
            <circle cx="152" cy="98" r="30" fill="url(#cloudFill)" />
          </g>

          {/* heart */}
          <g className="heart-beat" style={{ transformOrigin: "110px 88px" }}>
            <path
              d="M110,112 C95,99 82,90 82,77 C82,68 89,62 97,62 C103,62 108,66 110,71 C112,66 117,62 123,62 C131,62 138,68 138,77 C138,90 125,99 110,112 Z"
              fill="url(#heartFill)"
            />
          </g>
        </svg>
      </div>
    </div>
  );
}

export default function OrbitRing() {
  return (
    <div className="orbit" role="img" aria-label="One unified system: a heart in a cloud at the center, with appointments, records, pharmacy, labs, billing, QR payments, video visits and AI orbiting around it.">
      {/* orbit guide rings */}
      <div
        className="orbit-ring-line"
        style={{ width: "calc(var(--orbit-size) * 0.88)", height: "calc(var(--orbit-size) * 0.88)" }}
      />
      <div
        className="orbit-ring-line"
        style={{ width: "calc(var(--orbit-size) * 0.56)", height: "calc(var(--orbit-size) * 0.56)" }}
      />

      {/* outer ring */}
      <div className="orbit-ring orbit-spin">
        {OUTER.map((n, i) => (
          <div
            key={i}
            className="orbit-node"
            style={{ "--a": `${n.a}deg`, "--r": "calc(var(--orbit-size) * 0.44)" }}
          >
            <Chip icon={n.icon} tone={n.tone} size={54} counter="orbit-counter" />
          </div>
        ))}
      </div>

      {/* inner ring */}
      <div className="orbit-ring orbit-spin-rev">
        {INNER.map((n, i) => (
          <div
            key={i}
            className="orbit-node"
            style={{ "--a": `${n.a}deg`, "--r": "calc(var(--orbit-size) * 0.28)" }}
          >
            <Chip icon={n.icon} tone={n.tone} size={46} counter="orbit-counter-rev" />
          </div>
        ))}
      </div>

      <CloudHeart />
    </div>
  );
}
